import { ATTR_NAMES, getAttrFromProduct } from '../../types/attrNames';
import { CmsProduct } from '../../types/cmsProduct';
import { TravelPremiumsData } from '../../types/travelInsurance';
import { formatNumberWithSeparator } from '../../utils/helpers';

/**
 * Resolved value of the `TI_PREMIUM_PRICE` calculated attribute (CMS-1896).
 * Amounts are numbers, not formatted strings, so the consumer applies the
 * market's separator and prefix itself.
 */
export type TiPremiumPrice = {
  /** The price the customer pays. */
  amount: number;
  /** From the quote, falling back to the brand's `currencyDisplay`. */
  currencySymbol?: string;
  /** Pre-discount price. Present only when the quote carries a discount. */
  originalAmount?: number;
  /** Whole-percent discount, e.g. `25` renders as "25% off" / "25% 折扣". */
  discountPercent?: number;
  /**
   * The price ready to print — currency and amount — for a surface that shows
   * a calculated attribute as text. A price cell reads the parts instead.
   */
  formatted: string;
};

/**
 * Resolve `TI_PREMIUM_PRICE` for a product from the quotes at hand, matched by
 * `INSUREMO_PRODUCT_UID` — the same key every other price surface uses
 * (`SectionTravelInsurance`, `CardListingPosition`, `PlanTierSelector`).
 * Like those surfaces, no trip-type gate: a compare response for one trip type
 * carries no premiums for products of the other, so the UID match is the gate.
 *
 * `undefined` for anything unusable, which consumers render as their empty
 * state.
 */
export function resolveTiPremiumPrice(
  product: CmsProduct | undefined,
  premiums: TravelPremiumsData | undefined,
  /** Printed when the quote carries no currency symbol of its own. */
  brandCurrency?: string,
): TiPremiumPrice | undefined {
  if (!product || !premiums?.length) return undefined;

  const uid = getAttrFromProduct(
    ATTR_NAMES.INSUREMO_PRODUCT_UID,
    product,
  )?.value;

  if (!uid) return undefined;

  const quote = premiums.find((entry) => entry?.brokerProductCode === uid);
  if (!quote) return undefined;

  return resolveTiQuotePrice(quote, brandCurrency);
}

/**
 * The quote-level part of `resolveTiPremiumPrice`: amount, currency and the
 * discount one quote carries. Exported for a surface that already holds the
 * product's quote (the Sort By measure), so it shares this parsing and rounding.
 * `undefined` when the quote has no usable offered price.
 */
export function resolveTiQuotePrice(
  quote: Partial<
    Pick<
      NonNullable<TravelPremiumsData[number]>,
      'offeredPremium' | 'originalPremium' | 'currencySymbol'
    >
  >,
  brandCurrency?: string,
): TiPremiumPrice | undefined {
  const amount = toAmount(quote.offeredPremium);
  if (amount === undefined) return undefined;

  const currencySymbol = quote.currencySymbol;
  const originalAmount = toAmount(quote.originalPremium);
  const formatted = `${currencySymbol || brandCurrency || ''}${formatPremiumAmount(amount)}`;

  // Strictly higher: InsureMO echoes the offered premium into `originalPremium`
  // when nothing is discounted.
  const hasDiscount = originalAmount !== undefined && originalAmount > amount;
  if (!hasDiscount) return { amount, currencySymbol, formatted };

  const discountPercent = Math.round(
    ((originalAmount - amount) / originalAmount) * 100,
  );

  // A difference under 0.5% floors to 0, and a "0% off" badge is noise.
  if (discountPercent <= 0) return { amount, currencySymbol, formatted };

  return { amount, currencySymbol, originalAmount, discountPercent, formatted };
}

/**
 * Premiums arrive as strings; anything not a POSITIVE finite number means "no
 * quote". `Number('0')`, `Number(' ')` and `Number('-5')` are all finite, so
 * without `> 0` each renders as a real advertised price. See
 * `../cms-blocks/TableBuilder/README.md`.
 */
function toAmount(value: string | undefined): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  // `Number`, not `parseFloat`: `parseFloat('1,250.00')` is `1` and would render
  // a 1-dollar premium.
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
}

/**
 * A premium as it is shown: thousands separated and always two decimals, so a
 * discounted price and the struck-through original line up digit for digit.
 * Lives with the type rather than in a cell, because every surface that shows a
 * premium must show it the same way.
 */
export const formatPremiumAmount = (value: number): string =>
  formatNumberWithSeparator({
    value,
    thousandsSeparator: ',',
    decimalSeparator: '.',
    alwaysTwoDecimals: true,
  });

/**
 * The currency to print in front of a premium.
 *
 * `||`, not `??`: the API sends `currencySymbol: ''` as well as omitting it,
 * and an empty prefix would render an amount with no currency at all.
 */
export const premiumCurrency = (
  price: TiPremiumPrice | undefined,
  brandCurrency: string | undefined,
): string | undefined => price?.currencySymbol || brandCurrency;
